/**
 * Presentation Layer - Theme Switcher
 * UI component for switching between themes
 */

"use client";

import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslations } from "next-intl";
import { setTheme as setReduxTheme } from "@/shared/domain/store/slices/ui-slice";
import { cn } from "@/shared/core/utils";
import type { Theme } from "@/shared/core/types";
import { CSS_CLASSES } from "@/constants";
import { Button } from "../ui/button";

export function ThemeSwitcher({ className }: { className?: string }) {
  const { theme, setTheme } = useTheme();
  const dispatch = useDispatch();
  const t = useTranslations("aria");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (mounted && theme) {
      dispatch(setReduxTheme(theme as Theme));
    }
  }, [theme, mounted, dispatch]);

  if (!mounted) {
    return null;
  }

  return (
    <div className={cn("flex items-center gap-1", className)}>
      {/* Light Mode */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setTheme("light")}
        className={cn(
          "h-8 w-8 sm:h-9 sm:w-9",
          theme === "light"
            ? CSS_CLASSES.ACTIVE_BUTTON
            : CSS_CLASSES.INACTIVE_BUTTON_PRIMARY_HOVER
        )}
        aria-label={t("lightMode")}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="w-4 h-4 sm:w-5 sm:h-5"
        >
          <path d="M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" />
          <path d="M12 2v2" />
          <path d="M12 20v2" />
          <path d="m4.93 4.93 1.41 1.41" />
          <path d="m17.66 17.66 1.41 1.41" />
          <path d="M2 12h2" />
          <path d="M20 12h2" />
          <path d="m6.34 17.66-1.41 1.41" />
          <path d="m19.07 4.93-1.41 1.41" />
        </svg>
      </Button>

      {/* Dark Mode */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setTheme("dark")}
        className={cn(
          "h-8 w-8 sm:h-9 sm:w-9",
          theme === "dark"
            ? CSS_CLASSES.ACTIVE_BUTTON
            : CSS_CLASSES.INACTIVE_BUTTON_PRIMARY_HOVER
        )}
        aria-label={t("darkMode")}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="w-4 h-4 sm:w-5 sm:h-5"
        >
          <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
        </svg>
      </Button>

      {/* System Mode */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setTheme("system")}
        className={cn(
          "h-8 w-8 sm:h-9 sm:w-9",
          theme === "system"
            ? CSS_CLASSES.ACTIVE_BUTTON
            : CSS_CLASSES.INACTIVE_BUTTON_PRIMARY_HOVER
        )}
        aria-label={t("systemMode")}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="w-4 h-4 sm:w-5 sm:h-5"
        >
          <rect width="20" height="14" x="2" y="3" rx="2" />
          <line x1="8" x2="16" y1="21" y2="21" />
          <line x1="12" x2="12" y1="17" y2="21" />
        </svg>
      </Button>
    </div>
  );
}
